import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Spinner, ArrowLeft, Clock, Eye, PencilSimple, CheckCircle,
  FolderSimple, ArrowClockwise,
} from '@phosphor-icons/react';
import { useSubmissionStore } from '../../stores/submissionStore';
import { useAuthStore } from '../../stores/authStore';
import type { Submission, SubmissionStatus } from '@spine-workspace/shared';

const STATUS_CONFIG: Record<SubmissionStatus, { label: string; color: string; icon: React.ReactNode }> = {
  waiting_for_review: { label: 'Waiting',   color: 'text-amber-400 bg-amber-400/10 border-amber-400/20',  icon: <Clock size={10} weight="fill" /> },
  in_review:          { label: 'In Review',  color: 'text-blue-400 bg-blue-400/10 border-blue-400/20',    icon: <Eye size={10} weight="fill" /> },
  need_update:        { label: 'Needs Update', color: 'text-red-400 bg-red-400/10 border-red-400/20',     icon: <PencilSimple size={10} weight="fill" /> },
  approved:           { label: 'Approved',   color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', icon: <CheckCircle size={10} weight="fill" /> },
  cancelled:          { label: 'Cancelled',  color: 'text-text-tertiary bg-bg-input border-border',             icon: null },
};

const TABS: { key: SubmissionStatus | 'all'; label: string }[] = [
  { key: 'all',               label: 'All' },
  { key: 'waiting_for_review', label: 'Waiting' },
  { key: 'in_review',          label: 'In Review' },
  { key: 'need_update',        label: 'Needs Update' },
  { key: 'approved',           label: 'Approved' },
];

function timeAgo(iso: string) {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function ReviewBoard() {
  const navigate = useNavigate();
  const { submissions, loading, error, fetchSubmissions } = useSubmissionStore();
  const isAdmin = useAuthStore(s => s.user?.isAdmin ?? false);
  const [activeTab, setActiveTab] = useState<SubmissionStatus | 'all'>('all');

  useEffect(() => { fetchSubmissions(); }, []);

  const filtered = activeTab === 'all'
    ? submissions
    : submissions.filter(s => s.status === activeTab);

  const counts = Object.fromEntries(
    ['waiting_for_review', 'in_review', 'need_update', 'approved'].map(k => [
      k, submissions.filter(s => s.status === k).length,
    ])
  );

  function openReviewScene(sub: Submission) {
    if (sub.reviewSceneId) {
      navigate(`/scene/${sub.reviewSceneId}/edit`);
    }
  }

  return (
    <div className="min-h-screen bg-bg-deepest flex flex-col">
      {/* Header */}
      <header className="bg-bg-surface border-b border-border-subtle px-6 h-[52px] flex items-center gap-3 shrink-0">
        <button
          onClick={() => navigate('/dashboard')}
          className="w-8 h-8 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <img src="/icon-512.png" alt="" className="w-6 h-6 rounded shrink-0" />
        <span className="font-serif text-base font-medium text-text-primary">Review Queue</span>
        <span className="text-text-tertiary text-xs font-mono ml-1">{submissions.length} total</span>
        <button
          onClick={fetchSubmissions}
          className="ml-auto w-8 h-8 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors"
          title="Refresh"
        >
          <ArrowClockwise size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </header>

      {/* Tab bar */}
      <div className="bg-bg-surface border-b border-border-subtle px-6">
        <div className="flex gap-0">
          {TABS.map(({ key, label }) => {
            const count = key === 'all' ? submissions.length : (counts[key] ?? 0);
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 px-4 py-3 font-sans text-sm font-medium transition-all border-b-2 -mb-px ${
                  activeTab === key
                    ? 'text-accent border-accent'
                    : 'text-text-secondary hover:text-text-primary border-transparent'
                }`}
              >
                {label}
                {count > 0 && (
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${
                    activeTab === key ? 'bg-accent/20 text-accent' : 'bg-bg-hover text-text-tertiary'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading && submissions.length === 0 ? (
          <div className="flex justify-center py-20">
            <Spinner size={20} className="animate-spin text-text-tertiary" />
          </div>
        ) : error ? (
          <div className="text-red-400 text-sm text-center py-10">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-text-tertiary text-sm text-center py-16 font-sans">
            {activeTab === 'all' ? 'No submissions yet' : `No ${STATUS_CONFIG[activeTab as SubmissionStatus]?.label ?? ''} submissions`}
          </div>
        ) : (
          <div className="max-w-5xl mx-auto grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map(sub => (
              <SubmissionCard
                key={sub.id}
                submission={sub}
                isAdmin={isAdmin}
                onOpen={() => openReviewScene(sub)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── SubmissionCard ────────────────────────────────────────────

function SubmissionCard({
  submission: sub,
  isAdmin,
  onOpen,
}: {
  submission: Submission;
  isAdmin: boolean;
  onOpen: () => void;
}) {
  const { updateStatus } = useSubmissionStore();
  const [acting, setAct] = useState(false);
  const cfg = STATUS_CONFIG[sub.status];

  async function act(status: SubmissionStatus, notes?: string) {
    setAct(true);
    try { await updateStatus(sub.id, status, notes); }
    finally { setAct(false); }
  }

  return (
    <div
      className="bg-bg-surface border border-border rounded-xl overflow-hidden hover:border-accent/30 transition-colors cursor-pointer group"
      onClick={onOpen}
    >
      {/* Thumbnail area */}
      <div className="h-36 bg-bg-input flex items-center justify-center relative overflow-hidden">
        {sub.thumbnailUrl ? (
          <img src={sub.thumbnailUrl} alt="" className="w-full h-full object-contain" />
        ) : (
          <FolderSimple size={32} className="text-text-tertiary/40" weight="thin" />
        )}
        {/* Status badge */}
        <div className={`absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] font-mono font-semibold ${cfg.color}`}>
          {cfg.icon}
          {cfg.label}
        </div>
        {sub.resubmissionCount > 0 && (
          <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[9px] font-mono bg-bg-elevated border border-border text-text-secondary">
            v{sub.resubmissionCount + 1}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="px-3 py-2.5 space-y-1">
        <p className="font-sans text-sm font-medium text-text-primary truncate" title={sub.submissionName ?? sub.assetName ?? undefined}>
          {sub.submissionName ?? sub.assetName ?? sub.driveFolderName ?? sub.driveFolderId.slice(0, 12)}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-sans text-text-tertiary">
            {sub.submitterName ?? 'Unknown'} · {timeAgo(sub.submittedAt)}
          </span>
          {sub.reviewSceneId && (
            <span className="text-[9px] font-mono text-accent opacity-0 group-hover:opacity-100 transition-opacity">
              Open ↗
            </span>
          )}
        </div>
        {sub.reviewNotes && (
          <p className="text-[9px] font-sans text-text-tertiary italic truncate" title={sub.reviewNotes}>
            "{sub.reviewNotes}"
          </p>
        )}
      </div>

      {/* Admin quick-actions */}
      {isAdmin && sub.status !== 'approved' && (
        <div
          className="border-t border-border-subtle px-3 py-1.5 flex gap-1.5"
          onClick={e => e.stopPropagation()}
        >
          {sub.status === 'waiting_for_review' && (
            <button
              disabled={acting}
              onClick={() => act('in_review')}
              className="flex-1 py-1 text-[10px] font-sans rounded bg-blue-400/10 text-blue-400 hover:bg-blue-400/20 disabled:opacity-50 transition-colors"
            >
              Start Review
            </button>
          )}
          {sub.status === 'in_review' && (
            <>
              <button
                disabled={acting}
                onClick={() => act('approved')}
                className="flex-1 py-1 text-[10px] font-sans rounded bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20 disabled:opacity-50 transition-colors"
              >
                Approve
              </button>
              <button
                disabled={acting}
                onClick={() => act('need_update')}
                className="flex-1 py-1 text-[10px] font-sans rounded bg-red-400/10 text-red-400 hover:bg-red-400/20 disabled:opacity-50 transition-colors"
              >
                Needs Update
              </button>
            </>
          )}
          {sub.status === 'need_update' && (
            <span className="flex-1 py-1 text-[10px] font-sans text-center text-text-tertiary">
              Awaiting re-submission
            </span>
          )}
        </div>
      )}
    </div>
  );
}
